import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { catchError, tap } from 'rxjs';
import { RestTranslationService } from '../translations/services/rest-translation.service';
import { Translation } from '../translations/translation';

export class FetchAllTranslationsAction {
  static readonly type = '[Translation] TranslationsFetched';
}

export class UpdateCurrentLanguageTranslationsAction {
  static readonly type = '[Translation] CurrentLanguageTranslations Updated';
  constructor(public selectedLanguage: string) {}
}

export interface TranslationStateModel {
  loading: boolean;
  currentLanguage: string;
  currentLanguageTranslation: Translation;
  allLanguageTranslations: Array<Translation>;
}

export class ResetTranslationStateAction {
  static readonly type = '[Translation] Translations State Reset';
}

const defaultState = {
  loading: true,
  currentLanguage: 'English (US)',
  currentLanguageTranslation: undefined as Translation,
  allLanguageTranslations: [] as Translation[]
};

const getDefaultState = () => {
  return { ...defaultState };
};

@State<TranslationStateModel>({
  name: 'translations',
  defaults: getDefaultState()
})
@Injectable()
export class TranslationState {
  public restTranslationService = inject(RestTranslationService);
  constructor() {}

  @Action(FetchAllTranslationsAction)
  fetchAllTranslationsAction({ patchState, getState }: StateContext<TranslationStateModel>) {
    if (getState().allLanguageTranslations.length === 0) {
      patchState({ loading: true });
      return this.restTranslationService.getAllTranslations().pipe(
        tap((res) => {
          const allLanguageTranslations = res.data;
          const currentLanguageTranslations = allLanguageTranslations.find((t) => t.language === getState().currentLanguage);

          patchState({ currentLanguageTranslation: currentLanguageTranslations, allLanguageTranslations, loading: false });
        }),

        catchError((error) => {
          patchState(getDefaultState());
          throw error;
        })
      );
    }
  }

  @Action(UpdateCurrentLanguageTranslationsAction)
  updateCurrentLanguageTranslationsAction(ctx: StateContext<TranslationStateModel>, action: UpdateCurrentLanguageTranslationsAction) {
    ctx.patchState({ loading: true });

    const currentLanguageTranslation = ctx.getState().allLanguageTranslations.find((t) => t.language === action.selectedLanguage);

    ctx.patchState({ currentLanguage: action.selectedLanguage, currentLanguageTranslation });

    ctx.patchState({ loading: false });
  }

  @Action(ResetTranslationStateAction)
  resetTranslationStateAction(ctx: StateContext<TranslationStateModel>) {
    ctx.patchState(getDefaultState());
  }

  @Selector()
  static state(state: TranslationStateModel) {
    return state;
  }

  @Selector()
  static currentLanguageTranslations(state: TranslationStateModel) {
    return state.currentLanguageTranslation;
  }

  @Selector()
  static loading(state: TranslationStateModel) {
    return state.loading;
  }

  @Selector()
  static currentLanguage(state: TranslationStateModel) {
    return state.currentLanguage;
  }

  @Selector()
  static allLanguageTranslations(state: TranslationStateModel) {
    return state.allLanguageTranslations;
  }
}
